import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface SiteSummary {
  id: string;
  name: string;
  operator: string;
  state: string;
  mine_type: string;
  voltage_levels: string[];
  eem_name: string;
  status: string;
  substation_count: number;
  device_count: number;
  report_count: number;
  pending_changes: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const userRaw = localStorage.getItem('prot_user');
  const user = userRaw ? JSON.parse(userRaw) : null;

  const handleLogout = () => {
    localStorage.removeItem('prot_token');
    localStorage.removeItem('prot_user');
    navigate('/login');
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const sitesRes = await api.get('/api/sites');
        const siteList: SiteSummary[] = sitesRes.data;

        // Enrich each site with counts from detail endpoint
        const enriched = await Promise.all(
          siteList.map(async (site) => {
            try {
              const detailRes = await api.get(`/api/sites/${site.id}`);
              return { ...site, ...detailRes.data };
            } catch {
              return site;
            }
          })
        );
        setSites(enriched);
      } catch {
        setError('Failed to load sites. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img
            src="/logo-white.png"
            alt="JMB"
            style={{ height: 36, objectFit: 'contain' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>
              Protection Platform
            </div>
            <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>
              POWER SYSTEM PROTECTION MANAGEMENT
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {user && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{user.name || user.email}</div>
              <div style={{ fontSize: 11, color: '#8899aa' }}>{user.role || 'User'}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              padding: '7px 18px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <main style={{ flex: 1, padding: '32px' }}>
        <h2 style={{ color: '#1a1a2e', fontWeight: 700, fontSize: 22, margin: '0 0 8px' }}>
          Mine Sites
        </h2>
        <p style={{ color: '#666', fontSize: 14, margin: '0 0 28px' }}>
          Select a site to view its protection management hub.
        </p>

        {loading && (
          <div style={{ textAlign: 'center', color: '#888', padding: 60, fontSize: 15 }}>
            Loading sites...
          </div>
        )}

        {error && (
          <div style={{
            background: '#fff5f5',
            border: '1px solid #f44336',
            borderRadius: 8,
            color: '#f44336',
            padding: '14px 18px',
            marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
            gap: 24,
          }}>
            {sites.map((site) => (
              <SiteCard key={site.id} site={site} onClick={() => navigate(`/site/${site.id}`)} />
            ))}
          </div>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

const SiteCard: React.FC<{ site: SiteSummary; onClick: () => void }> = ({ site, onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  const statusColor = site.status === 'active' ? '#4caf50' : '#ff9800';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: hovered
          ? '0 8px 30px rgba(15,52,96,0.16)'
          : '0 2px 12px rgba(0,0,0,0.07)',
        border: hovered ? '1.5px solid #0f3460' : '1.5px solid #e8eaf0',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'all 0.2s',
      }}
    >
      {/* Card header */}
      <div style={{
        background: '#0f3460',
        padding: '20px 22px',
        color: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{site.name}</div>
            <div style={{ fontSize: 13, color: '#a0c0dc' }}>
              {site.operator} · {site.state}
            </div>
          </div>
          <span style={{
            background: statusColor + '33',
            color: statusColor,
            border: `1px solid ${statusColor}66`,
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
          }}>
            {site.status}
          </span>
        </div>
      </div>

      {/* Open site action */}
      <div style={{
        padding: '14px 22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}>
        <span style={{
          fontSize: 13,
          color: '#0f3460',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}>
          Open site →
        </span>
      </div>
    </div>
  );
};

export default Dashboard;
